import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Home, Mail, Phone, Calendar, Filter, Search, CheckCircle, Eye } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Message {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  message: string;
  status: string;
  created_at: string;
  read_at: string | null;
  notes: string | null;
}

interface CaseManagerRequest {
  id: string;
  name: string;
  email: string;
  phone: string;
  preferred_contact: string;
  message: string | null;
  status: string;
  created_at: string;
  contacted_at: string | null;
  notes: string | null;
  assigned_to: string | null;
}

export default function AdminMessagesScreen() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'messages' | 'requests'>('messages');
  const [messages, setMessages] = useState<Message[]>([]);
  const [requests, setRequests] = useState<CaseManagerRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState<Message | CaseManagerRequest | null>(null);

  useEffect(() => {
    checkAuth();
    fetchData();
  }, [activeTab]);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate('/admin-login');
    }
  };

  const fetchData = async () => {
    setLoading(true);

    if (activeTab === 'messages') {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching messages:', error);
      } else {
        setMessages(data || []);
      }
    } else {
      const { data, error } = await supabase
        .from('case_manager_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching requests:', error);
      } else {
        setRequests(data || []);
      }
    }

    setLoading(false);
  };

  const markAsRead = async (id: string) => {
    const { error } = await supabase
      .from('messages')
      .update({ status: 'read', read_at: new Date().toISOString() })
      .eq('id', id);

    if (!error) {
      fetchData();
    }
  };

  const markAsContacted = async (id: string) => {
    const { error } = await supabase
      .from('case_manager_requests')
      .update({ status: 'contacted', contacted_at: new Date().toISOString() })
      .eq('id', id);

    if (!error) {
      fetchData();
    }
  };

  const updateStatus = async (id: string, status: string) => {
    const table = activeTab === 'messages' ? 'messages' : 'case_manager_requests';
    const { error } = await supabase
      .from(table)
      .update({ status })
      .eq('id', id);

    if (!error) {
      fetchData();
      setSelectedItem(null);
    }
  };

  const updateNotes = async (id: string, notes: string) => {
    const table = activeTab === 'messages' ? 'messages' : 'case_manager_requests';
    const { error } = await supabase
      .from(table)
      .update({ notes })
      .eq('id', id);

    if (!error) {
      fetchData();
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredMessages = messages.filter(msg => {
    const matchesStatus = statusFilter === 'all' || msg.status === statusFilter;
    const matchesSearch = msg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      msg.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      msg.message.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const filteredRequests = requests.filter(req => {
    const matchesStatus = statusFilter === 'all' || req.status === statusFilter;
    const matchesSearch = req.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (req.message && req.message.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesStatus && matchesSearch;
  });

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      new: 'bg-blue-100 text-blue-700',
      read: 'bg-yellow-100 text-yellow-700',
      responded: 'bg-green-100 text-green-700',
      contacted: 'bg-yellow-100 text-yellow-700',
      in_progress: 'bg-purple-100 text-purple-700',
      completed: 'bg-green-100 text-green-700',
      archived: 'bg-gray-100 text-gray-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-md sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/admin-dashboard" className="flex items-center space-x-3 group">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center transform group-hover:scale-110 transition-transform">
                <Home className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                Admin Dashboard
              </span>
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Messages & Requests</h1>
          <p className="text-gray-600">Manage incoming messages and case manager requests</p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex space-x-4 mb-6">
            <button
              onClick={() => setActiveTab('messages')}
              className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-all ${
                activeTab === 'messages'
                  ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              General Messages ({messages.length})
            </button>
            <button
              onClick={() => setActiveTab('requests')}
              className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-all ${
                activeTab === 'requests'
                  ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Case Manager Requests ({requests.length})
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by name, email, or message..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:ring focus:ring-amber-200 transition-all"
              />
            </div>

            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:ring focus:ring-amber-200 transition-all appearance-none"
              >
                <option value="all">All Statuses</option>
                {activeTab === 'messages' ? (
                  <>
                    <option value="new">New</option>
                    <option value="read">Read</option>
                    <option value="responded">Responded</option>
                    <option value="archived">Archived</option>
                  </>
                ) : (
                  <>
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="archived">Archived</option>
                  </>
                )}
              </select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        ) : (
          <div className="space-y-4">
            {activeTab === 'messages' ? (
              filteredMessages.length === 0 ? (
                <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                  <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">No messages found</h3>
                  <p className="text-gray-500">Messages will appear here when people contact you</p>
                </div>
              ) : (
                filteredMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow border-l-4 border-amber-500 overflow-hidden"
                  >
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-xl font-bold text-gray-800">{msg.name}</h3>
                            <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(msg.status)}`}>
                              {msg.status}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-3 text-sm text-gray-600">
                            <span className="flex items-center">
                              <Mail className="w-4 h-4 mr-1" />
                              {msg.email}
                            </span>
                            {msg.phone && (
                              <span className="flex items-center">
                                <Phone className="w-4 h-4 mr-1" />
                                {msg.phone}
                              </span>
                            )}
                            <span className="flex items-center">
                              <Calendar className="w-4 h-4 mr-1" />
                              {formatDate(msg.created_at)}
                            </span>
                          </div>
                        </div>
                        <div className="flex space-x-2 ml-4">
                          {msg.status === 'new' && (
                            <button
                              onClick={() => markAsRead(msg.id)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Mark as read"
                            >
                              <Eye className="w-5 h-5" />
                            </button>
                          )}
                          <button
                            onClick={() => setSelectedItem(msg)}
                            className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors text-sm font-semibold"
                          >
                            Manage
                          </button>
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-gray-700 whitespace-pre-wrap">{msg.message}</p>
                      </div>
                      {msg.notes && (
                        <div className="mt-4 bg-yellow-50 rounded-lg p-4 border-l-4 border-yellow-400">
                          <p className="text-sm font-semibold text-gray-700 mb-1">Admin Notes:</p>
                          <p className="text-gray-600 whitespace-pre-wrap">{msg.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )
            ) : (
              filteredRequests.length === 0 ? (
                <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                  <Phone className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">No requests found</h3>
                  <p className="text-gray-500">Case manager requests will appear here</p>
                </div>
              ) : (
                filteredRequests.map((req) => (
                  <div
                    key={req.id}
                    className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow border-l-4 border-orange-500 overflow-hidden"
                  >
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-xl font-bold text-gray-800">{req.name}</h3>
                            <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(req.status)}`}>
                              {req.status}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-3 text-sm text-gray-600">
                            <span className="flex items-center">
                              <Mail className="w-4 h-4 mr-1" />
                              {req.email}
                            </span>
                            <span className="flex items-center">
                              <Phone className="w-4 h-4 mr-1" />
                              {req.phone}
                            </span>
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold">
                              Prefers: {req.preferred_contact}
                            </span>
                            <span className="flex items-center">
                              <Calendar className="w-4 h-4 mr-1" />
                              {formatDate(req.created_at)}
                            </span>
                          </div>
                        </div>
                        <div className="flex space-x-2 ml-4">
                          {req.status === 'new' && (
                            <button
                              onClick={() => markAsContacted(req.id)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="Mark as contacted"
                            >
                              <CheckCircle className="w-5 h-5" />
                            </button>
                          )}
                          <button
                            onClick={() => setSelectedItem(req)}
                            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm font-semibold"
                          >
                            Manage
                          </button>
                        </div>
                      </div>
                      {req.message && (
                        <div className="bg-gray-50 rounded-lg p-4 mb-4">
                          <p className="text-sm font-semibold text-gray-700 mb-1">Additional Info:</p>
                          <p className="text-gray-700 whitespace-pre-wrap">{req.message}</p>
                        </div>
                      )}
                      {req.assigned_to && (
                        <div className="mb-4">
                          <span className="text-sm font-semibold text-gray-700">Assigned to: </span>
                          <span className="text-gray-600">{req.assigned_to}</span>
                        </div>
                      )}
                      {req.notes && (
                        <div className="bg-yellow-50 rounded-lg p-4 border-l-4 border-yellow-400">
                          <p className="text-sm font-semibold text-gray-700 mb-1">Admin Notes:</p>
                          <p className="text-gray-600 whitespace-pre-wrap">{req.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )
            )}
          </div>
        )}
      </div>

      {selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-800">Manage {activeTab === 'messages' ? 'Message' : 'Request'}</h2>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                <select
                  value={selectedItem.status}
                  onChange={(e) => updateStatus(selectedItem.id, e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:ring focus:ring-amber-200 transition-all"
                >
                  {activeTab === 'messages' ? (
                    <>
                      <option value="new">New</option>
                      <option value="read">Read</option>
                      <option value="responded">Responded</option>
                      <option value="archived">Archived</option>
                    </>
                  ) : (
                    <>
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="archived">Archived</option>
                    </>
                  )}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Admin Notes</label>
                <textarea
                  value={selectedItem.notes || ''}
                  onChange={(e) => setSelectedItem({ ...selectedItem, notes: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:ring focus:ring-amber-200 transition-all resize-none"
                  placeholder="Add internal notes about this contact..."
                />
              </div>

              <div className="flex space-x-4">
                <button
                  onClick={() => {
                    updateNotes(selectedItem.id, selectedItem.notes || '');
                    setSelectedItem(null);
                  }}
                  className="flex-1 bg-gradient-to-r from-amber-500 to-orange-600 text-white py-3 rounded-lg font-semibold hover:from-amber-600 hover:to-orange-700 transition-all"
                >
                  Save Changes
                </button>
                <button
                  onClick={() => setSelectedItem(null)}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
